/**
 * @fileoverview CSI Widget Component
 * Displays the CAP Stress Index with regime, pillars, and top drivers.
 * Gothic Terminal Design System compliant (ADR-0004).
 * @module csi/components/CSIWidget
 */

import { el } from '../../shared/components/simpleRender.js';
import { CSI_SPEC } from '../spec.js';

// Import styles
import '../styles/csi-widget.css';

/** @type {string[]} Pillar order for display */
const PILLAR_ORDER = CSI_SPEC.PILLAR_ORDER;

// ============================================================================
// DISPLAY CONFIGURATION
// ============================================================================

/**
 * Score decimal precision for display.
 * Higher precision looks more "official" and data-driven.
 * @type {number}
 */
const SCORE_PRECISION = 2; // 50.13 instead of 50

/**
 * Pillar score precision.
 * @type {number}
 */
const PILLAR_PRECISION = 1; // 62.4 instead of 62

/**
 * Delta/change precision.
 * @type {number}
 */
const DELTA_PRECISION = 2; // +3.21 instead of +3.2

// ============================================================================
// REGIME CONFIGURATION (Gothic Terminal Colors)
// ============================================================================

/**
 * Regime display configuration using CSS custom properties.
 * @type {Record<string, {label: string, color: string, bg: string, description: string}>}
 */
export const REGIME_CONFIG = {
  CALM: {
    label: 'CALM',
    color: 'var(--tg-fog-green)',
    bg: 'rgba(151, 184, 160, 0.15)',
    description: 'Markets stable, low stress'
  },
  NORMAL: {
    label: 'NORMAL',
    color: 'var(--tg-fog-green)',
    bg: 'rgba(151, 184, 160, 0.15)',
    description: 'Typical market conditions'
  },
  ELEVATED: {
    label: 'ELEVATED',
    color: 'var(--tg-lantern-gold)',
    bg: 'rgba(212, 168, 95, 0.15)',
    description: 'Rising stress indicators'
  },
  HIGH: {
    label: 'HIGH',
    color: 'var(--tg-rust-red)',
    bg: 'rgba(178, 51, 42, 0.15)',
    description: 'Significant stress detected'
  },
  CRISIS: {
    label: 'CRISIS',
    color: 'var(--tg-flash-crash)',
    bg: 'rgba(255, 0, 51, 0.15)',
    description: 'Extreme stress conditions'
  }
};

/**
 * Pillar display names.
 * @type {Record<string, string>}
 */
export const PILLAR_NAMES = {
  FUNDING: 'Funding',
  LIQUIDITY: 'Liquidity',
  RATES_CURVE: 'Rates & Curve',
  CREDIT: 'Credit',
  VOL_EQUITY: 'Equity Vol',
  VOL_RATES: 'Rates Vol'
};

// ============================================================================
// COMPONENT CREATION
// ============================================================================

/**
 * Creates a CSI Widget component.
 * @param {Object} config - Widget configuration
 * @param {import('../types.js').CSIResult} [config.data] - CSI result data
 * @param {boolean} [config.compact=false] - Compact display mode
 * @param {boolean} [config.showDrivers=true] - Show top drivers section
 * @param {Function} [config.onDriverClick] - Callback when driver is clicked
 * @returns {{element: HTMLElement, update: Function, destroy: Function}}
 */
export function createCSIWidget(config = {}) {
  const {
    data = null,
    compact = false,
    showDrivers = true,
    onDriverClick = null
  } = config;

  // Track event listeners for cleanup
  /** @type {Array<{element: HTMLElement, type: string, handler: Function}>} */
  const eventListeners = [];

  // Create main container
  const element = el('div', { 
    className: `csi-widget ${compact ? 'csi-widget--compact' : ''}`,
    'data-component': 'csi-widget'
  }, []);

  // Initial render
  if (data) {
    renderWidget(element, data, { compact, showDrivers, onDriverClick, eventListeners });
  } else {
    renderLoading(element);
  }

  /**
   * Updates the widget with new data.
   * @param {import('../types.js').CSIResult} newData
   */
  function update(newData) {
    // Clean up old event listeners before re-render
    cleanupEventListeners(eventListeners);
    renderWidget(element, newData, { compact, showDrivers, onDriverClick, eventListeners });
  }

  /**
   * Destroys the widget and cleans up resources.
   */
  function destroy() {
    cleanupEventListeners(eventListeners);
    element.innerHTML = '';
    console.log('[CSIWidget] Destroyed');
  }

  return { element, update, destroy };
}

/**
 * Cleans up tracked event listeners.
 * @param {Array<{element: HTMLElement, type: string, handler: Function}>} listeners
 */
function cleanupEventListeners(listeners) {
  for (const { element, type, handler } of listeners) {
    element.removeEventListener(type, handler);
  }
  listeners.length = 0; // Clear the array
}

/**
 * Registers an event listener and tracks it for cleanup.
 * @param {HTMLElement} element
 * @param {string} type
 * @param {Function} handler
 * @param {Array} listeners - Listeners array to track
 */
function addTrackedListener(element, type, handler, listeners) {
  element.addEventListener(type, handler);
  listeners.push({ element, type, handler });
}

/**
 * Renders loading state.
 * @param {HTMLElement} container
 */
function renderLoading(container) {
  container.innerHTML = '';
  container.appendChild(
    el('div', { className: 'csi-widget__loading' }, [
      el('span', { className: 'csi-widget__loading-text' }, ['Computing CSI...'])
    ])
  );
}

/**
 * Renders the widget with data.
 * @param {HTMLElement} container
 * @param {import('../types.js').CSIResult} data
 * @param {Object} options
 */
function renderWidget(container, data, options) {
  container.innerHTML = '';
  
  const { compact, showDrivers, onDriverClick, eventListeners } = options;
  const regimeCfg = REGIME_CONFIG[data.regime] || REGIME_CONFIG.NORMAL;

  // Build widget structure
  const children = [
    // Header with score and regime
    createHeader(data, regimeCfg, compact),
    
    // Pillar bars
    createPillarSection(data.pillars, compact),
  ];

  // Top drivers (unless compact or disabled)
  if (showDrivers && !compact && data.topDrivers?.length > 0) {
    children.push(createDriversSection(data.topDrivers, onDriverClick, eventListeners));
  }

  // Metadata footer
  children.push(createFooter(data, compact));

  for (const child of children) {
    if (child) container.appendChild(child);
  }
}

/**
 * Creates the header section with score and regime.
 * @param {import('../types.js').CSIResult} data
 * @param {Object} regimeCfg
 * @param {boolean} compact
 * @returns {HTMLElement}
 */
function createHeader(data, regimeCfg, compact) {
  const scoreColor = getScoreColor(data.score);
  const formattedScore = formatScore(data.score, SCORE_PRECISION);
  
  return el('div', { className: 'csi-widget__header' }, [
    // Left: Title and regime
    el('div', { className: 'csi-widget__header-left' }, [
      el('div', { className: 'csi-widget__title-row' }, [
        el('h3', { className: 'csi-widget__title' }, ['CAP Stress Index']),
        el('span', { 
          className: 'csi-widget__regime',
          style: `color: ${regimeCfg.color}; background: ${regimeCfg.bg}; border-color: ${regimeCfg.color}`
        }, [regimeCfg.label]),
      ]),
      !compact ? el('p', { className: 'csi-widget__regime-desc' }, [regimeCfg.description]) : null,
    ].filter(Boolean)),
    
    // Right: Score display
    el('div', { className: 'csi-widget__header-right' }, [
      el('div', { className: 'csi-widget__score-container' }, [
        el('span', { 
          className: 'csi-widget__score',
          style: `color: ${scoreColor}`
        }, [formattedScore]),
        el('span', { className: 'csi-widget__score-max' }, ['/100']),
      ]),
      !compact ? createDynamics(data) : null,
    ].filter(Boolean)),
  ]);
}

/**
 * Creates the dynamics display (impulse/acceleration).
 * @param {import('../types.js').CSIResult} data
 * @returns {HTMLElement}
 */
function createDynamics(data) {
  const impulseDir = data.impulse >= 0 ? 'up' : 'down';
  const impulseSign = data.impulse >= 0 ? '+' : '';
  const impulseFormatted = formatScore(data.impulse, DELTA_PRECISION);
  
  return el('div', { className: 'csi-widget__dynamics' }, [
    el('span', { className: `csi-widget__impulse csi-widget__impulse--${impulseDir}` }, [
      `${impulseSign}${impulseFormatted} vs EMA`
    ]),
  ]);
}

/**
 * Creates the pillar bars section.
 * @param {Record<string, import('../types.js').PillarResult>} pillars
 * @param {boolean} compact
 * @returns {HTMLElement}
 */
function createPillarSection(pillars, compact) {
  const pillarElements = PILLAR_ORDER.map(pillarId => {
    const pillar = pillars[pillarId];
    if (!pillar) return null;
    
    return createPillarBar(pillarId, pillar, compact);
  }).filter(Boolean);

  return el('div', { className: 'csi-widget__pillars' }, pillarElements);
}

/**
 * Creates a single pillar bar.
 * @param {string} pillarId
 * @param {import('../types.js').PillarResult} pillar
 * @param {boolean} compact
 * @returns {HTMLElement}
 */
function createPillarBar(pillarId, pillar, compact) {
  const score = pillar.score;
  const barColor = getPillarBarGradient(score);
  const width = Math.min(100, Math.max(0, score));
  const formattedScore = formatScore(score, PILLAR_PRECISION);
  
  return el('div', { 
    className: `csi-widget__pillar ${pillar.isDegraded ? 'csi-widget__pillar--degraded' : ''}`,
    title: `${pillar.signalCount} signals, ${pillar.familyCount} families, ${(pillar.confidence * 100).toFixed(0)}% confidence`
  }, [
    el('div', { className: 'csi-widget__pillar-label' }, [
      el('span', { className: 'csi-widget__pillar-name' }, [PILLAR_NAMES[pillarId] || pillarId]),
      !compact ? el('span', { className: 'csi-widget__pillar-score' }, [formattedScore]) : null,
    ].filter(Boolean)),
    el('div', { className: 'csi-widget__pillar-bar' }, [
      el('div', { 
        className: 'csi-widget__pillar-fill',
        style: `width: ${width}%; background: ${barColor}`
      }, []),
    ]),
  ]);
}

/**
 * Creates the top drivers section.
 * @param {import('../types.js').TopDriver[]} drivers
 * @param {Function|null} onDriverClick
 * @param {Array} eventListeners - Listeners array to track
 * @returns {HTMLElement}
 */
function createDriversSection(drivers, onDriverClick, eventListeners) {
  const topN = drivers.slice(0, 5); // Show top 5 in widget

  return el('div', { className: 'csi-widget__drivers' }, [
    el('div', { className: 'csi-widget__drivers-header' }, [
      el('span', { className: 'csi-widget__drivers-title' }, ['Top Stress Drivers']),
    ]),
    el('div', { className: 'csi-widget__drivers-list' }, 
      topN.map(driver => createDriverRow(driver, onDriverClick, eventListeners))
    ),
  ]);
}

/**
 * Creates a single driver row.
 * @param {import('../types.js').TopDriver} driver
 * @param {Function|null} onClick
 * @param {Array} eventListeners - Listeners array to track
 * @returns {HTMLElement}
 */
function createDriverRow(driver, onClick, eventListeners) {
  const deltaClass = driver.delta20d >= 0 ? 'csi-widget__driver-delta--up' : 'csi-widget__driver-delta--down';
  const deltaSign = driver.delta20d >= 0 ? '+' : '';
  const contribFormatted = formatScore(driver.contribution, PILLAR_PRECISION);
  const deltaFormatted = formatScore(Math.abs(driver.delta20d), DELTA_PRECISION);
  
  const row = el('div', { 
    className: 'csi-widget__driver',
    'data-signal-id': driver.signalId
  }, [
    el('span', { className: 'csi-widget__driver-name' }, [driver.name || driver.signalId]),
    el('span', { className: 'csi-widget__driver-pillar' }, [PILLAR_NAMES[driver.pillar] || driver.pillar]),
    el('span', { className: 'csi-widget__driver-contrib' }, [contribFormatted]),
    el('span', { className: `csi-widget__driver-delta ${deltaClass}` }, [
      `${deltaSign}${deltaFormatted}`
    ]),
  ]);

  if (onClick) {
    row.style.cursor = 'pointer';
    const handler = () => onClick(driver);
    addTrackedListener(row, 'click', handler, eventListeners);
  }

  return row;
}

/**
 * Creates the footer with metadata.
 * @param {import('../types.js').CSIResult} data
 * @param {boolean} compact
 * @returns {HTMLElement}
 */
function createFooter(data, compact) {
  const { signalCount, staleCount, degradedPillars } = data.metadata;
  const confidenceFormatted = formatScore(data.confidence * 100, PILLAR_PRECISION);
  
  return el('div', { className: 'csi-widget__footer' }, [
    el('span', { className: 'csi-widget__meta' }, [
      `${signalCount} signals • ${confidenceFormatted}% conf`
    ]),
    staleCount > 0 ? el('span', { className: 'csi-widget__stale' }, [
      `${staleCount} stale`
    ]) : null,
    el('span', { className: 'csi-widget__date' }, [data.date]),
  ].filter(Boolean));
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Formats a score with specified precision.
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
function formatScore(value, decimals) {
  if (!isFinite(value)) return '—';
  return value.toFixed(decimals);
}

/**
 * Gets CSS color variable for score value.
 * Uses Gothic Terminal design tokens.
 * @param {number} score
 * @returns {string}
 */
function getScoreColor(score) {
  if (score >= 80) return 'var(--tg-flash-crash)';  // Crisis
  if (score >= 65) return 'var(--tg-rust-red)';     // High
  if (score >= 50) return 'var(--tg-lantern-gold)'; // Elevated
  if (score >= 35) return 'var(--tg-fog-green)';    // Normal
  return 'var(--tg-fog-green)';                      // Calm
}

/**
 * Gets gradient for pillar bar based on score.
 * Uses Gothic Terminal colors.
 * @param {number} score
 * @returns {string}
 */
function getPillarBarGradient(score) {
  // Use actual hex values for gradients (CSS variables don't work in gradients reliably)
  if (score >= 70) return 'linear-gradient(90deg, #B2332A, #FF0033)';     // Rust to Flash
  if (score >= 55) return 'linear-gradient(90deg, #D4A85F, #B2332A)';     // Gold to Rust
  if (score >= 40) return 'linear-gradient(90deg, #97B8A0, #D4A85F)';     // Fog to Gold
  return 'linear-gradient(90deg, #6B8F71, #97B8A0)';                       // Dark Fog to Fog
}
